import { PageHeader, Section, Overline, PrimaryCTA } from "@/components/Section";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  return (
    <>
      <PageHeader
        overline="About"
        title="Built by the team behind GeoLens."
        lede="Continuum is a product of The Creative Coefficient. The platform is led by Chantal Hansen with the analyst team that has delivered evidence work across the Canadian public sector since 2019."
        testid="about-page-header"
      />

      <Section testid="about-founder">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="border border-slate-200 p-8 bg-[#F8F9FA]">
              <Overline className="mb-6">Founder</Overline>
              <h2 className="text-3xl font-black tracking-tighter text-[#0A1224]">Chantal Hansen</h2>
              <p className="text-slate-600 mt-2">Founder, The Creative Coefficient · Continuum</p>
              <div className="mt-8 pt-8 border-t border-slate-300 space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Chantal leads sector-facing engagements at GeoLens and is the principal
                  signatory on Continuum's statutory outputs.
                </p>
                <p>
                  Her professional sign-off is what makes a Continuum Housing Needs Report a
                  compliant statutory document, not a dashboard export.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Overline className="mb-6">Why this team is building this</Overline>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A1224] leading-tight mb-8">
              Fifteen client engagements. One recurring failure mode. <span className="text-[#FF8A00]">One platform built to end it.</span>
            </h2>
            <div className="space-y-5 text-slate-700 text-lg leading-relaxed">
              <p>
                The Creative Coefficient is the corporate home for two complementary practices:
                GeoLens — a Canadian research and evidence consultancy — and Continuum, the
                platform that turns the recurring parts of that consulting work into an
                inspectable, versionable data product.
              </p>
              <p>
                Continuum did not start as a product idea. It started as a notebook of every
                time a client asked the same question we had answered six months earlier for
                a different community. The product is what falls out of refusing to redo that
                work — and refusing to lock its results behind a consulting engagement.
              </p>
              <p>
                Continuum is built by the team behind GeoLens, a Canadian research and
                evidence consultancy.{" "}
                <a
                  href="https://geo-lens.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0A1224] font-bold underline underline-offset-4 hover:text-[#FF8A00] inline-flex items-center gap-1"
                >
                  Visit geo-lens.com <ArrowUpRight size={16} />
                </a>
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section testid="about-principles" className="bg-[#0A1224] text-white">
        <Overline className="text-slate-400 mb-6">House rules</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight mb-12 max-w-4xl">
          Brand separation is a standing rule.
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {[
            {
              t: "Two practices, one team",
              d: "GeoLens sells consulting. Continuum sells platform. We don't pitch the platform inside a consulting proposal, and we don't pitch consulting on a SaaS landing page.",
            },
            {
              t: "Cross-link sparingly",
              d: "One reference each way, once Continuum launches publicly. No shared navigation. No pooled testimonials.",
            },
            {
              t: "Client work stays client work",
              d: "We only publish what's been explicitly approved by the client. No screenshots, no excerpts, no logos without written permission.",
            },
          ].map((b, i) => (
            <div key={i} data-testid={`about-principle-${i}`} className="bg-[#0A1224] p-8">
              <div className="text-[#FF8A00] text-xs font-black tracking-[0.2em] uppercase">Rule 0{i + 1}</div>
              <h3 className="text-xl font-black tracking-tight mt-3">{b.t}</h3>
              <p className="text-slate-300 mt-3 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section testid="about-cta" className="text-center">
        <Overline className="mb-6">Talk to us directly</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto leading-tight">
          The walkthrough is with a person, not a sales bot.
        </h2>
        <div className="mt-10">
          <PrimaryCTA to="/contact" testid="about-final-cta">Book a walkthrough</PrimaryCTA>
        </div>
      </Section>
    </>
  );
}
